import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../modal/ModalShell";
import { AdminUser, adminUserDisplayName } from "../../features/admin/models/AdminUser";

interface ChangeLoginEmailModalProps {
  user: AdminUser;
  busy: boolean;
  onApply: (newEmail: string, resetCredentials: boolean) => void;
  onClose: () => void;
}

/**
 * Hands an account over to a new owner by changing its login email. With the
 * default handover reset, the password and social sign-ins are wiped and the
 * new address receives a set-your-password link — the previous owner keeps no
 * way in. Unchecked, only the email changes (typo fixes).
 */
const ChangeLoginEmailModal: React.FC<ChangeLoginEmailModalProps> = ({
  user,
  busy,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const [newEmail, setNewEmail] = useState("");
  const [resetCredentials, setResetCredentials] = useState(true);

  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail.trim());

  return (
    <ModalShell onClose={onClose} labelledBy="change-login-email-title">
      <div className="p-6">
        <h2 id="change-login-email-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("admin.changeEmail.title", "Change login email")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t("admin.changeEmail.subtitle", {
            name: adminUserDisplayName(user),
            defaultValue: "Hand {{name}}'s account over by moving it to a new login email.",
          })}
        </p>
        <p className="mt-2 truncate text-sm text-grey-40 dark:text-grey-70">
          {t("admin.changeEmail.currentLabel", "Current")}: <code>{user.email}</code>
        </p>

        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("admin.changeEmail.newEmailLabel", "New login email")}
          <input
            type="email"
            className="input mt-1"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            placeholder="new.owner@example.com"
            autoFocus
          />
        </label>

        <label className="mt-4 flex items-start gap-2 text-sm text-grey-30 dark:text-grey-70">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={resetCredentials}
            onChange={(event) => setResetCredentials(event.target.checked)}
          />
          <span>
            <span className="font-medium text-grey-20 dark:text-white">
              {t("admin.changeEmail.resetLabel", "Reset credentials for handover")}
            </span>
            <br />
            {t(
              "admin.changeEmail.resetHint",
              "Removes the password and any linked Google / Microsoft / Apple sign-ins, and emails the new address a set-your-password link. Uncheck only for typo fixes.",
            )}
          </span>
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !valid}
            onClick={() => onApply(newEmail.trim(), resetCredentials)}
          >
            {t("admin.changeEmail.apply", "Change email")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ChangeLoginEmailModal;
